import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { getFeaturePlan, getTaskProductDocument } from "../api";
import AcceptanceReviewSection from "../AcceptanceReviewSection";
import InlineAlert from "../ui/InlineAlert";
import Skeleton from "../ui/Skeleton";
import "./FocusedReviewPage.css";

function specContent(payload) {
  return payload?.document?.content || payload?.content || null;
}

function specItems(content) {
  if (!content) return [];
  const items = [];
  if (content.outcome) items.push(content.outcome);
  for (const requirement of content.requirements || []) {
    const title = requirement?.title || requirement?.text || "";
    if (!title) continue;
    items.push(requirement.explanation ? `${title}. ${requirement.explanation}` : title);
  }
  for (const criterion of content.successChecks || []) {
    if (criterion?.text) items.push(criterion.text);
  }
  return items;
}

export default function FocusedReviewPage({ repo, taskId, experimental = false }) {
  const [plan, setPlan] = useState(null);
  const [spec, setSpec] = useState(null);
  const [context, setContext] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setError("");
    Promise.all([
      getFeaturePlan(taskId),
      getTaskProductDocument(taskId).catch(() => null),
    ]).then(([loaded, document]) => {
      if (cancelled) return;
      setPlan(loaded || null);
      const content = specContent(document);
      setSpec(content);
      const note = loaded?.userNotes || "";
      setContext(note);
      setDraft(note);
    }).catch(() => {
      if (!cancelled) setError("This review is not available in this workspace.");
    });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  async function saveContext() {
    setSaving(true);
    try {
      const saved = await api.put(`/feature-plans/${taskId}`, { userNotes: draft });
      const note = saved.data?.userNotes ?? draft;
      setContext(note);
      setDraft(note);
      setEditing(false);
    } catch {
      setError("Context could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  if (error && !plan) {
    return (
      <div className="rr-page rr-focused-review">
        <InlineAlert tone="alert">{error}</InlineAlert>
        <Link to="/reviews">Back to reviews</Link>
      </div>
    );
  }

  if (!plan) return <Skeleton label="Loading review" />;

  const title = plan.title || spec?.title || plan.featureRequestText || "Review";
  const items = specItems(spec);
  const criteria = Array.isArray(plan.acceptanceCriteria) ? plan.acceptanceCriteria.filter(Boolean) : [];

  return (
    <div className="rr-page rr-focused-review" data-testid="focused-review">
      <header className="rr-page-head">
        <div>
          <p className="rr-kicker">{repo?.owner}/{repo?.name}</p>
          <h1>{title}</h1>
        </div>
        <Link to="/reviews">Back to reviews</Link>
      </header>

      <section className="rr-review-block" data-testid="review-context">
        <h2>Context</h2>
        {context && !editing ? <p data-testid="review-context-text">{context}</p> : null}
        {!context && !editing ? <p>Add what you are trying to do, if this review needs it.</p> : null}
        {editing ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              saveContext();
            }}
          >
            <label>
              What this review is for
              <textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={4} />
            </label>
            <button type="submit" className="rr-button" disabled={saving}>{saving ? "Saving…" : "Save context"}</button>
          </form>
        ) : (
          <button type="button" className="rr-button rr-button-quiet" onClick={() => setEditing(true)}>
            {context ? "Edit context" : "Add context"}
          </button>
        )}
      </section>

      <section className="rr-review-block" data-testid="review-spec">
        <h2>Spec</h2>
        {items.length ? (
          <ul>
            {items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        ) : criteria.length ? (
          <ul>
            {criteria.map((item) => <li key={item}>{item}</li>)}
          </ul>
        ) : (
          <p>No spec is attached to this review.</p>
        )}
      </section>

      <section className="rr-review-block" data-testid="review-decision">
        <AcceptanceReviewSection
          featurePlanId={taskId}
          planStatus={plan.status}
          planTitle={title}
          repoId={repo?._id}
          reviewEligible
          heading="Decision review"
          intro="Whether this change matches the spec."
          hideSourceForm
        />
      </section>

      {experimental ? (
        <p className="rr-muted">
          <Link to={`/repositories/${repo?._id}/tasks/${taskId}`}>Open the task workspace</Link>
        </p>
      ) : null}
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
    </div>
  );
}
